import { combineReducers, AnyAction } from "redux";
import { PagedResponse } from "@ofqual/portal-core/core/types";

import {
  LOADED_NOTIFICATIONS,
  LOADING_NOTIFICATIONS,
  TOGGLE_NOTIFICATION_PANEL,
  TOGGLE_BULK_MODE,
  UPDATE_PARAMS,
  TOGGLE_SHOULD_CLEAR_NOTIFICATION,
  CLEAR_ALL_SHOULD_CLEAR_NOTIFICATION,
  READ_NOTIFICATION_BULK,
  READ_NOTIFICATION,
  PUSH_NOTIFICATION
} from "./actions";
import {
  NotificationsDataState,
  NotificationsViewState,
  defaultDataState,
  defaultViewState,
} from "./state";

const dataState = (
  state: NotificationsDataState = defaultDataState,
  action: AnyAction
): NotificationsDataState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_NOTIFICATIONS:
      return {
        ...state,
        status: "LOADING",
      };
    case LOADED_NOTIFICATIONS:
      return {
        ...state,
        status: "LOADED",
        response: payload,
        hasData: true,
      };
    case UPDATE_PARAMS:
      return {
        ...state,
        params: { ...payload },
      };
    case PUSH_NOTIFICATION:
      return {
        ...state,
        response: {
          ...state.response,
          data: [payload, ...state.response.data],
          count: state.response.count + 1
        }
      };
    default:
      return state;
  }
};

const viewState = (
  state: NotificationsViewState = defaultViewState,
  action: AnyAction
): NotificationsViewState => {
  const { type, payload } = action;
  switch (type) {
    case TOGGLE_NOTIFICATION_PANEL:
      return {
        ...state,
        isOpen: payload,
      };
    case TOGGLE_BULK_MODE:
      return {
        ...state,
        isBulkMode: payload,
      };
    case TOGGLE_SHOULD_CLEAR_NOTIFICATION:
      const { ids, shouldClear } = payload as {
        ids: string[];
        shouldClear: boolean;
      };

      let nextNotificationsToClear = [...state.notificationsToClear];
      for (const id of ids) {
        if (shouldClear) {
          if (!nextNotificationsToClear.includes(id)) {
            nextNotificationsToClear = [...nextNotificationsToClear, id];
          }
        } else {
          nextNotificationsToClear = nextNotificationsToClear.filter(
            (existingId) => existingId !== id
          );
        }
      }
      return {
        ...state,
        notificationsToClear: nextNotificationsToClear,
      };
    case CLEAR_ALL_SHOULD_CLEAR_NOTIFICATION:
    case READ_NOTIFICATION_BULK:
      return {
        ...state,
        notificationsToClear: [],
      };
    case READ_NOTIFICATION:
      const id = payload as string;
      // edge case: if a notification has been read directly (not through bulk clear)
      //  but it has also been checked to be included in bulk clear, remove it from the array.
      if (state.notificationsToClear.includes(id)) {
        return {
          ...state,
          notificationsToClear: state.notificationsToClear.filter(
            (existingId) => existingId !== id
          ),
        };
      } else {
        return state;
      }
    case LOADED_NOTIFICATIONS: {
      if ((payload as PagedResponse<never>).count === 0) {
        // if we have no items anymore then it is illogical to still be in bulk mode
        //  as we do not allow transition into bulk mode if there are no items
        return {
          ...state,
          isBulkMode: false,
        };
      } else {
        return state;
      }
    }
    default:
      return state;
  }
};

export const rootReducer = combineReducers({
  dataState,
  viewState,
});
